import {_serializeSingle, _serializeList, _serializeExcept} from '../core';
import type {ServerData, ServerRecord} from '../core';
import type {DomainOf} from '../../../../types/domain';

/** 스키마 밖에서 조립되는 인스턴스 BFF 필드 */
type DocumentInstanceBffExtras = {
    documentInstanceDatas?: DomainOf<"documentInstanceData">[];
    /** output 존재 여부 */
    downloadable?: boolean;
    /** convertStatus ∈ {FAILURE, COMPLETE} */
    reconvertable?: boolean;
};

export function serializeDocumentInstanceData(data: ServerData) {
    return _serializeSingle("documentInstanceData", data);
}

export function serializeDocumentInstanceDataList(data: ServerData) {
    return _serializeList("documentInstanceData", data);
}

export function serializeDocumentInstance(data: ServerData) {
    return _serializeSingle("documentInstance", data);
}

export function serializeDocumentInstanceList(data: ServerData) {
    return _serializeList("documentInstance", data);
}

/**
 * `instance/bff/search` · `instance/{instanceId}` 응답
 * 인스턴스 기본 필드에 데이터 배열과 다운로드·재변환 플래그 포함
 */
export function serializeDocumentInstanceBff(data: ServerData) {
    if (!data) return null;
    const instance: DomainOf<"documentInstance"> & DocumentInstanceBffExtras = _serializeSingle("documentInstance", data);

    if (data.documentInstanceDatas) {
        instance.documentInstanceDatas = data.documentInstanceDatas
            .map((d: ServerRecord) => serializeDocumentInstanceData(d));
    }
    if (data.downloadable !== undefined) instance.downloadable = data.downloadable;
    if (data.reconvertable !== undefined) instance.reconvertable = data.reconvertable;

    return instance;
}

export function serializeDocumentInstanceBffList(data: ServerData) {
    if (!data || !Array.isArray(data)) return [];
    return data.map((item: ServerRecord) => serializeDocumentInstanceBff(item));
}

/**
 * 워크스페이스 내 전체 인스턴스 검색 — `admin/document/instance/bff/search`
 */

/** 응답이 싣지 않는 document 필드 */
const INSTANCE_SEARCH_DOCUMENT_OMIT = [
    "form", "serverForm", "documentDraftId", "documentTemplateId", "departmentId",
    "attribute.imported", "attribute.autoCompleted", "attribute.enabledInterPageSeal",
    "attribute.enabledViewPassword", "attribute.viewPassword", "attribute.viewPasswordHint",
    "attribute.enabledViewPasswordOnComplete", "attribute.enabledApproverFullApproval",
    "attribute.enabledExternalDocument", "attribute.externalDocumentReason",
    "attribute.enabledManualSealDate", "attribute.manualSealDate",
    "statusDate.startedDate", "statusDate.requestedDate", "statusDate.revokedDate",
    "statusDate.deletedDate", "statusDate.sealedDate",
    "statusDate.convertStartedDate", "statusDate.convertEndedDate",
    "workspaceId", "updatedDate",
] as const;

/** 응답이 싣지 않는 documentSeal 필드 */
const INSTANCE_SEARCH_SEAL_OMIT = [
    "sealFileSize", "sealFileHash",
    "documentId", "workspaceId", "createdDate", "updatedDate",
] as const;

/** 응답의 라벨이 싣지 않는 label 필드 */
const INSTANCE_SEARCH_LABEL_OMIT = [
    "externalId", "description", "order", "enabledRemark",
    "workspaceId", "createdDate", "updatedDate",
] as const;

/** 라벨 배열 */
function serializeInstanceSearchLabels(labels: unknown) {
    if (!Array.isArray(labels)) return undefined;
    return labels.map((label: ServerRecord) => _serializeExcept("label", {
        ...label,
        id: label.labelId,
    }, INSTANCE_SEARCH_LABEL_OMIT));
}

/** 응답의 member 가 싣지 않는 documentParticipant 필드 */
const INSTANCE_SEARCH_PARTICIPANT_OMIT = [
    "id", "documentId",
    "memberExternalStateId", "memberExternalStateName", "memberExternalUpdatedDate",
    "memberCellPhone", "memberAddress", "memberBirthDate", "memberJoinDate",
    "memberQuitDate", "memberProfileUrl", "memberRole", "memberState",
    "externalStateId", "externalStateName", "externalUpdatedDate",
    "cellPhone", "address", "birthDate", "joinDate", "quitDate", "profileUrl",
    "role", "state",
    "departmentExternalId", "departmentCode", "departmentDescription", "departmentOrder",
    "departmentStartDate", "departmentEndDate", "departmentParentId", "departmentMemberRole",
    "workspaceId", "createdDate",
] as const;

const INSTANCE_SEARCH_REQUESTER_OMIT = [
    "documentId", "workspaceId", "createdDate", "updatedDate",
] as const;

const INSTANCE_SEARCH_SEAL_MANAGER_OMIT = [
    "documentId", "documentParticipantActorType", "workspaceId", "createdDate", "updatedDate",
] as const;

/** 참여자 */
function serializeInstanceSearchParticipant(member: ServerData) {
    if (!member) return null;
    return _serializeExcept("documentParticipant", {
        ...member,
        number: member.memberNumber,
        name: member.memberName,
        email: member.memberEmail,
        phone: member.memberPhone,
    }, INSTANCE_SEARCH_PARTICIPANT_OMIT);
}

/** 요청자·날인자 — 참여 정보 + member(라벨 포함)를 한 객체로 평탄화 */
function serializeInstanceSearchParty(
    party: ServerRecord,
    domainName: "documentRequester" | "documentSealManager",
    omit: readonly string[],
) {
    const labels = serializeInstanceSearchLabels(party.member?.labels);
    return {
        ..._serializeExcept(domainName, party, omit),
        ...serializeInstanceSearchParticipant(party.member),
        ...(labels ? {labels} : {}),
    };
}

export function serializeDocumentInstanceSearchBff(data: ServerData) {
    if (!data) return null;

    const row: Record<string, unknown> = {
        // 부모 문서 → 인스턴스 순서. 평탄 id 는 행 식별자인 인스턴스가 가져간다
        ...(data.document ? _serializeExcept("document", {
            ...data.document,
            // 서버는 완료 일시를 평탄하게 내려주지만 도메인·정렬 키는 statusDate 하위다
            statusDate: {completedDate: data.document.completedDate},
        }, INSTANCE_SEARCH_DOCUMENT_OMIT) : {}),
        ..._serializeSingle("documentInstance", data),
    };

    // 문서 라벨(문서 유형·요청 사유) — 행 최상위에서 ColumnLabel 이 읽는다
    const documentLabels = serializeInstanceSearchLabels(data.document?.labels);
    if (documentLabels) row.labels = documentLabels;

    if (data.documentSeal) {
        const sealLabels = serializeInstanceSearchLabels(data.documentSeal.labels);
        row.documentSeal = {
            ..._serializeExcept("documentSeal", data.documentSeal, INSTANCE_SEARCH_SEAL_OMIT),
            ...(sealLabels ? {labels: sealLabels} : {}),
        };
    }
    if (data.documentRequester) {
        row.documentRequester = serializeInstanceSearchParty(
            data.documentRequester, "documentRequester", INSTANCE_SEARCH_REQUESTER_OMIT);
    }
    if (data.documentSealManager) {
        row.documentSealManager = serializeInstanceSearchParty(
            data.documentSealManager, "documentSealManager", INSTANCE_SEARCH_SEAL_MANAGER_OMIT);
    }

    return row;
}
